import type { Data } from 'plotly.js';

export interface ChartLegendRow {
  name: string;
  color: string;
  value: number | null;
  hidden: boolean;
  legendParts: Array<{ key: string; label: string; value: string }> | null;
}

export type ChartLegendSortDirection = 'asc' | 'desc';

export function chartDatumToMs(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const parsed = Date.parse(value.replace(' ', 'T'));
  return Number.isFinite(parsed) ? parsed : null;
}

function numericValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function valueAtCursor(trace: Data, cursorMs: number | null): number | null {
  const x = Array.from((trace as { x?: Iterable<unknown> }).x ?? []);
  const y = Array.from((trace as { y?: Iterable<unknown> }).y ?? []);
  if (x.length === 0 || y.length === 0) return null;

  if (cursorMs === null) {
    for (let index = y.length - 1; index >= 0; index -= 1) {
      const value = numericValue(y[index]);
      if (value !== null) return value;
    }
    return null;
  }

  const length = Math.min(x.length, y.length);
  let low = 0;
  let high = length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const timestamp = chartDatumToMs(x[middle]);
    if (timestamp !== null && timestamp < cursorMs) low = middle + 1;
    else high = middle;
  }

  const candidates = [low - 1, low];
  let bestValue: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const index of candidates) {
    if (index < 0 || index >= length) continue;
    const timestamp = chartDatumToMs(x[index]);
    const value = numericValue(y[index]);
    if (timestamp === null || value === null) continue;
    const distance = Math.abs(timestamp - cursorMs);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestValue = value;
    }
  }
  return bestValue;
}

function traceColor(trace: Data): string {
  const lineColor = (trace as { line?: { color?: unknown } }).line?.color;
  if (typeof lineColor === 'string') return lineColor;
  const markerColor = (trace as { marker?: { color?: unknown } }).marker?.color;
  return typeof markerColor === 'string' ? markerColor : '#888';
}

function traceLegendParts(trace: Data): ChartLegendRow['legendParts'] {
  const parts = (trace as {
    meta?: { legendParts?: Array<{ key?: unknown; label?: unknown; value?: unknown }> };
  }).meta?.legendParts;
  if (!Array.isArray(parts)) return null;
  return parts
    .filter((part) => typeof part.key === 'string' && typeof part.label === 'string')
    .map((part) => ({
      key: String(part.key),
      label: String(part.label),
      value: String(part.value ?? 'Unknown'),
    }));
}

export function buildChartLegendRows(
  traces: Data[],
  cursorMs: number | null,
  sortDirection: ChartLegendSortDirection = 'desc',
): ChartLegendRow[] {
  return traces
    .map((trace, index) => ({
      name: String((trace as { name?: unknown }).name ?? `Series ${index + 1}`),
      color: traceColor(trace),
      value: valueAtCursor(trace, cursorMs),
      hidden: (trace as { visible?: unknown }).visible === 'legendonly',
      legendParts: traceLegendParts(trace),
      index,
    }))
    .sort((left, right) => {
      if (left.value === null) return right.value === null ? left.index - right.index : 1;
      if (right.value === null) return -1;
      const difference = sortDirection === 'asc'
        ? left.value - right.value
        : right.value - left.value;
      return difference || left.index - right.index;
    })
    .map(({ index: _index, ...row }) => row);
}
